import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChannelScopeEnum, EconomicalValueProviderEnum } from '@/features/broadcasters/types';
import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { TEconomicalValuesForm } from '../types';

import { economicalValuesQueryKeys } from './query-keys';

export type TPatchEconomicalValuesParams = {
  file: File;
  provider: EconomicalValueProviderEnum;
  scope: ChannelScopeEnum;
  country_code: string;
  region_code: string;
  start_datetime: Date | null;
  media_type: string;
};

export async function patchEconomicalValues(
  body: TPatchEconomicalValuesParams,
  onProgress: (progress: number) => void
): Promise<TEconomicalValuesForm> {
  const formData = new FormData();
  formData.append('file', body.file);
  formData.append('provider', body.provider);
  formData.append('scope', body.scope);
  formData.append('country_code', body.country_code);
  formData.append('region_code', body.region_code);
  formData.append('media_type', body.media_type);
  if (body.start_datetime) formData.append('start_datetime', body.start_datetime.toISOString());

  return mediaManagerClient.patch('/api/v1/economical_values/files', formData, {
    onUploadProgress: progressEvent => {
      if (progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
}

type MutationFn = (
  body: TPatchEconomicalValuesParams,
  onProgress: (progress: number) => void
) => Promise<TEconomicalValuesForm>;

type TUsePatchEconomicalValuesParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchEconomicalValues({ config = {} }: TUsePatchEconomicalValuesParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    ...config,
    mutationFn: (body: TPatchEconomicalValuesParams) =>
      patchEconomicalValues(body, setUploadProgress),
    onError(error) {
      addErrorNotification(t('No se ha podido cargar el archivo'));
      console.error(error);
    },
    onSuccess() {
      addSuccessNotification(t('El archivo se ha cargado correctamente'));
      queryClient.invalidateQueries({ queryKey: economicalValuesQueryKeys.all });
      setUploadProgress(0);
    },
  });

  return { ...mutation, uploadProgress };
}
